import React, { useEffect, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input, Label, Textarea } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../components/ui/table";
import { toast } from "sonner";
import { Flame, Pencil, Plus, MapPin, Check, X } from "lucide-react";
import { PageShell } from "./PageShell";

export default function PlatformHazards() {
  const [hazards, setHazards] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editHz, setEditHz] = useState(null);
  const [editLoc, setEditLoc] = useState(null);
  const [newLoc, setNewLoc] = useState(null);
  const [covReqs, setCovReqs] = useState([]);
  const [fulfillReq, setFulfillReq] = useState(null);

  const load = () => {
    api.get("/hazards/catalog").then((r) => setHazards(r.data.hazards)).catch(() => {});
    api.get("/hazards/locations").then((r) => setLocations(r.data.locations)).catch(() => {});
    api.get("/hazards/admin/coverage-requests").then((r) => setCovReqs(r.data.requests)).catch(() => {});
  };
  useEffect(load, []);

  const saveHz = async () => {
    try {
      await api.put(`/hazards/admin/catalog/${editHz.code}`, { pathway: editHz.pathway, business_impact: editHz.business_impact });
      toast.success("Hazard updated"); setEditHz(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveLoc = async (loc, isNew) => {
    try {
      const body = { name: loc.name, country: loc.country, lat: +loc.lat, lng: +loc.lng, levels: Object.fromEntries(Object.entries(loc.levels || {}).map(([k, v]) => [k, +v])) };
      let key = loc.key;
      if (isNew) {
        const r = await api.post("/hazards/admin/locations", body);
        key = r.data.key;
      } else await api.put(`/hazards/admin/locations/${loc.key}`, body);
      toast.success(isNew ? "Location added" : "Location updated");
      if (isNew && fulfillReq) {
        const fr = await api.post(`/hazards/admin/coverage-requests/${fulfillReq.id}/fulfill`, { location_key: key });
        toast.success(`Coverage request fulfilled — ${fr.data.facilities_placed} facilit${fr.data.facilities_placed === 1 ? "y" : "ies"} re-matched; requesters notified`);
        setFulfillReq(null);
      }
      setEditLoc(null); setNewLoc(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const decline = async (r) => {
    const reason = window.prompt("Reason for declining (sent to the requesting tenant):", "Outside current coverage roadmap");
    if (reason === null) return;
    try {
      await api.post(`/hazards/admin/coverage-requests/${r.id}/decline`, { reason });
      toast.success("Request declined — tenant notified"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <PageShell title="Hazard Catalog Console" testid="platform-hazards-page"
      subtitle="Shared reference data every tenant reads: the four-hazard catalog and curated location hazard levels. Tenants view — only the operator edits."
      actions={<Button variant="primary" size="sm" onClick={() => setNewLoc({ name: "", country: "", lat: 0, lng: 0, levels: {} })} data-testid="ph-new-location-btn"><Plus className="h-3.5 w-3.5" /> New location</Button>}>
      {covReqs.length > 0 && (
        <div className="card-flat p-4" data-testid="ph-coverage-requests">
          <div className="label-eyebrow mb-2 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Tenant coverage requests ({covReqs.filter((r) => r.status === "pending").length} pending)</div>
          {covReqs.map((r) => (
            <div key={r.id} className="py-2 border-b border-border last:border-0 text-xs flex items-start justify-between gap-3 flex-wrap" data-testid={`ph-covreq-${r.id}`}>
              <div>
                <div className="font-medium">
                  {r.area_name} <span className="font-mono text-[10px] text-muted-foreground">({r.lat}, {r.lng})</span>
                  <a href={`https://www.google.com/maps?q=${r.lat},${r.lng}`} target="_blank" rel="noopener noreferrer" className="ml-1.5 text-[10px] underline decoration-dotted hover:text-accent" data-testid={`ph-covreq-map-${r.id}`}>map</a>
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {(r.requesters || []).map((q, i) => <span key={i}>{q.tenant_name} · {q.email} · {q.facility_name}{i < r.requesters.length - 1 ? "  |  " : ""}</span>)}
                </div>
              </div>
              {r.status === "pending" ? (
                <div className="flex gap-2">
                  <Button size="sm" variant="primary" className="h-6 text-[10px]" data-testid={`ph-covreq-approve-${r.id}`}
                    onClick={() => { setFulfillReq(r); setNewLoc({ name: r.area_name, country: "Singapore", lat: r.lat, lng: r.lng, levels: {} }); }}>
                    <Check className="h-3 w-3" /> Approve — add location
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => decline(r)} data-testid={`ph-covreq-decline-${r.id}`}><X className="h-3 w-3" /> Decline</Button>
                </div>
              ) : (
                <Badge variant={r.status === "fulfilled" ? "success" : "danger"} className="text-[9px]">{r.status}{r.decline_reason ? ` — ${r.decline_reason}` : ""}</Badge>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        {hazards.map((h) => (
          <div key={h.code} className="card-flat p-4" data-testid={`ph-hazard-${h.code}`}>
            <div className="flex items-center gap-2">
              <Flame className="h-4 w-4 text-accent" /><Badge variant="lime" className="font-mono text-[10px]">{h.code}</Badge>
              <span className="font-display font-bold">{h.name}</span>
              <Button size="sm" variant="ghost" className="ml-auto h-6 text-[10px]" onClick={() => setEditHz({ ...h })} data-testid={`ph-edit-${h.code}`}><Pencil className="h-2.5 w-2.5" /> Edit</Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">{h.pathway}</p>
            <p className="text-xs text-muted-foreground mt-1">{h.business_impact}</p>
          </div>
        ))}
      </div>
      <div className="card-flat overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Location</TableHead><TableHead>Country</TableHead><TableHead>Lat/Lng</TableHead>{hazards.map((h) => <TableHead key={h.code}>{h.code}</TableHead>)}<TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {locations.map((l) => (
              <TableRow key={l.key} data-testid={`ph-loc-${l.key}`}>
                <TableCell className="text-xs font-medium">{l.name}</TableCell>
                <TableCell className="text-xs">{l.country}</TableCell>
                <TableCell className="font-mono text-[10px]">{l.lat}, {l.lng}</TableCell>
                {hazards.map((h) => <TableCell key={h.code} className="font-mono text-xs">{(l.levels || {})[h.code] || 0}</TableCell>)}
                <TableCell><Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setEditLoc({ ...l })} data-testid={`ph-edit-loc-${l.key}`}><Pencil className="h-2.5 w-2.5" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editHz} onOpenChange={(o) => !o && setEditHz(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit {editHz?.code} {editHz?.name}</DialogTitle></DialogHeader>
          <div><Label>Pathway</Label><Textarea rows={2} value={editHz?.pathway || ""} onChange={(e) => setEditHz({ ...editHz, pathway: e.target.value })} data-testid="ph-hz-pathway" /></div>
          <div><Label>Business impact</Label><Textarea rows={2} value={editHz?.business_impact || ""} onChange={(e) => setEditHz({ ...editHz, business_impact: e.target.value })} data-testid="ph-hz-impact" /></div>
          <Button variant="primary" onClick={saveHz} data-testid="ph-hz-save">Save</Button>
        </DialogContent>
      </Dialog>
      <Dialog open={!!editLoc} onOpenChange={(o) => !o && setEditLoc(null)}>
        <DialogContent><DialogHeader><DialogTitle>Edit {editLoc?.name}</DialogTitle></DialogHeader>
          {editLoc && <LocForm loc={editLoc} setLoc={setEditLoc} isNew={false} hazards={hazards} onSave={saveLoc} />}</DialogContent>
      </Dialog>
      <Dialog open={!!newLoc} onOpenChange={(o) => { if (!o) { setNewLoc(null); setFulfillReq(null); } }}>
        <DialogContent><DialogHeader><DialogTitle>{fulfillReq ? `New curated location — fulfilling request for ${fulfillReq.area_name}` : "New curated location"}</DialogTitle></DialogHeader>
          {newLoc && <LocForm loc={newLoc} setLoc={setNewLoc} isNew hazards={hazards} onSave={saveLoc} />}</DialogContent>
      </Dialog>
    </PageShell>
  );
}

function LocForm({ loc, setLoc, isNew, hazards, onSave }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Name *</Label><Input value={loc.name} onChange={(e) => setLoc({ ...loc, name: e.target.value })} data-testid="ph-loc-name" /></div>
        <div><Label>Country *</Label><Input value={loc.country} onChange={(e) => setLoc({ ...loc, country: e.target.value })} data-testid="ph-loc-country" /></div>
        <div><Label>Latitude</Label><Input type="number" step="0.01" value={loc.lat} onChange={(e) => setLoc({ ...loc, lat: e.target.value })} data-testid="ph-loc-lat" /></div>
        <div><Label>Longitude</Label><Input type="number" step="0.01" value={loc.lng} onChange={(e) => setLoc({ ...loc, lng: e.target.value })} data-testid="ph-loc-lng" /></div>
        {hazards.map((h) => (
          <div key={h.code}><Label>{h.code} {h.name} (0-5)</Label>
            <Input type="number" min="0" max="5" value={(loc.levels || {})[h.code] ?? 0}
              onChange={(e) => setLoc({ ...loc, levels: { ...loc.levels, [h.code]: e.target.value } })} data-testid={`ph-loc-level-${h.code}`} /></div>
        ))}
      </div>
      <Button variant="primary" onClick={() => onSave(loc, isNew)} disabled={!loc.name || !loc.country} data-testid="ph-loc-save">Save</Button>
    </>
  );
}
